import { useEffect, useState, useCallback } from 'react';
import { KeyRound, Check, AlertTriangle, Loader2, Copy } from 'lucide-react';

/**
 * Org-level license card for AdminSettings.
 *
 * Visible only when the user has an organizationId (org admins, platform
 * admins). Hides itself silently for users without org context.
 *
 * - Displays current org license status: tier, licensee, expiry, modules,
 *   tenant count.
 * - Provides a paste-and-activate textarea so org admins can apply a JWT
 *   key without going through Platform Admin.
 */
const MODULE_LIST = [
  ['cameras',             'Cameras'],
  ['access_control',      'Access Control'],
  ['intercoms',           'Intercoms'],
  ['monitoring',          'Monitoring'],
  ['recording_assurance', 'Recording Assurance'],
  ['entry',               'Entry'],
  ['reports',             'Reports'],
];

const formatDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

const OrgLicenseCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState(null);
  const [showActivate, setShowActivate] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/org-license', { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) { setStatus({ noOrg: true }); return; }
        throw new Error(`HTTP ${res.status}`);
      }
      setStatus(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const activate = async () => {
    setError(null);
    if (!keyInput.trim()) { setError('Paste a license key'); return; }
    setActivating(true);
    try {
      const res = await fetch('/api/org-license/activate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: keyInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setKeyInput('');
      setShowActivate(false);
      await fetchStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setActivating(false);
    }
  };

  if (loading) return null;
  if (status?.noOrg) return null; // user has no org context, hide card entirely

  const hasLicense = status?.hasLicense;

  return (
    <div className="card mt-4 org-license-card">
      <div className="org-license-card-header">
        <div>
          <h3><KeyRound size={16} /> Organization License</h3>
          <p className="org-license-card-sub">
            {hasLicense
              ? 'Umbrella license that defines module ceilings across all your sites.'
              : 'No organization license active. Paste a key to activate.'}
          </p>
        </div>
        <button className="btn btn-secondary btn-small" onClick={() => setShowActivate(s => !s)}>
          {showActivate ? 'Cancel' : (hasLicense ? 'Replace Key' : 'Activate Key')}
        </button>
      </div>

      {error && (
        <div className="org-license-error" style={{ marginTop: 12 }}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {hasLicense && (
        <div className="org-license-card-body">
          <div className="org-license-card-meta">
            <div><span className="muted">Licensee:</span> <strong>{status.licensee}</strong></div>
            <div><span className="muted">Tier:</span> <strong>{status.tier}</strong></div>
            <div><span className="muted">Status:</span> <strong>{status.status}</strong></div>
            <div><span className="muted">Expires:</span> <strong>{formatDate(status.expiresAt)}</strong></div>
            <div><span className="muted">Tenants:</span> <strong>{status.tenantsUsed} / {status.maxTenants}</strong></div>
          </div>

          <table className="org-license-modules-table">
            <thead>
              <tr><th>Module</th><th>Status</th><th style={{ textAlign: 'right' }}>Used / Limit</th></tr>
            </thead>
            <tbody>
              {MODULE_LIST.map(([key, label]) => {
                const m = status.modules?.[key];
                if (!m) return null;
                const enabled = m.enabled;
                const limit = m.limit;
                const used = m.used ?? 0;
                const pct = enabled && typeof limit === 'number' && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                return (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>{enabled ? <span className="status-pill ok">enabled</span> : <span className="status-pill off">disabled</span>}</td>
                    <td style={{ textAlign: 'right' }}>
                      {enabled
                        ? (typeof limit === 'number' ? (limit === -1 ? `${used} / ∞` : `${used} / ${limit}`) : '—')
                        : '—'}
                      {enabled && typeof limit === 'number' && limit > 0 && (
                        <div className="org-license-progress">
                          <div className="org-license-progress-bar" style={{ width: `${pct}%`, background: pct >= 90 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e' }} />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showActivate && (
        <div className="org-license-activate" style={{ marginTop: 12 }}>
          <textarea
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            placeholder="Paste your JWT license key..."
            rows={5}
            className="org-license-key-display"
          />
          <button
            className="btn btn-primary"
            onClick={activate}
            disabled={activating || !keyInput.trim()}
            style={{ marginTop: 8 }}
          >
            {activating ? <><Loader2 size={14} className="spin" /> Activating...</> : <><Check size={14} /> Activate</>}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrgLicenseCard;
